import { forwardRef, useCallback } from "react";
import type { ChangeEvent, FocusEvent } from "react";

type NumberInputProps = {
  id?: string;
  name: string;
  defaultValue?: string | number;
  value?: string | number;
  placeholder?: string;
  required?: boolean;
  hasError?: boolean;
  min?: number;
  max?: number;
  step?: number;
  /** Allow decimal numbers (default: false — integers only) */
  allowDecimal?: boolean;
  autoComplete?: string;
  dir?: "ltr" | "rtl" | "auto";
  className?: string;
  disabled?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
};

/**
 * A number input that uses `type="text"` with `inputMode="numeric"` or `"decimal"`
 * to avoid the browser's native number input scroll-to-change-value behavior.
 *
 * - Filters out non-numeric characters on input
 * - Allows a single decimal point when `allowDecimal` is true
 * - Allows a leading minus sign for negative numbers
 * - Shows the numeric keyboard on mobile via `inputMode`
 * - Exposes the same API as TextInput for easy migration
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    {
      id,
      name,
      defaultValue,
      value,
      placeholder,
      required,
      hasError,
      min,
      max,
      step,
      allowDecimal = false,
      autoComplete,
      dir,
      className,
      disabled,
      onChange,
      onBlur,
    },
    ref
  ) {
    const isControlled = value !== undefined;

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        // Allow empty string (clearing the field)
        if (raw === "") {
          onChange?.(e);
          return;
        }

        // Allow just a minus sign (user is typing a negative number)
        if (raw === "-") {
          onChange?.(e);
          return;
        }

        // Allow just a decimal point start like ".5" or "-."
        if (allowDecimal && (raw === "." || raw === "-.")) {
          onChange?.(e);
          return;
        }

        // Validate: only digits, optional leading minus, optional single decimal
        const pattern = allowDecimal
          ? /^-?\d*\.?\d*$/
          : /^-?\d*$/;

        if (!pattern.test(raw)) {
          // Reject the input — don't call onChange, don't update the value
          e.preventDefault();
          return;
        }

        onChange?.(e);
      },
      [allowDecimal, onChange]
    );

    const stringValue =
      value !== undefined && value !== null ? String(value) : undefined;
    const stringDefault =
      defaultValue !== undefined && defaultValue !== null
        ? String(defaultValue)
        : undefined;

    return (
      <input
        ref={ref}
        id={id}
        name={name}
        type="text"
        inputMode={allowDecimal ? "decimal" : "numeric"}
        defaultValue={isControlled ? undefined : stringDefault}
        value={stringValue}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        autoComplete={autoComplete ?? "off"}
        dir={dir}
        aria-invalid={hasError ? "true" : undefined}
        onChange={handleChange}
        onBlur={onBlur}
        className={`h-[35px] w-full rounded-md border bg-[color:var(--surface)] px-3 text-[13px] focus:outline-none ${
          hasError
            ? "border-[color:var(--error-500)] focus:border-[color:var(--error-500)]"
            : "border-[color:var(--border-light)] focus:border-[color:var(--primary-500)]"
        } ${disabled ? "cursor-not-allowed opacity-60" : ""} ${className ?? ""}`}
      />
    );
  }
);
